import { Body, Button, ButtonGroup, H2, H3 } from "@datapay-by-datacom/design-system";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pagination } from "../components/Pagination/Pagination";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  PAY_COMPONENT_IMPORT_BASE_PATH,
  clampPageSize,
} from "../config";
import { useCompanyMappings } from "../hooks/useCompanyMappings";
import { useComponentsParameters } from "../hooks/useComponentsParameters";
import { usePagination } from "../hooks/usePagination";
import { useWizard } from "../hooks/useWizard";
import { newImportWizardState } from "../types";
import { clearWizardSession } from "../utils/wizardSession";
import { distinctComponentNames } from "../utils/componentNames";
import { buildMappedRows } from "../utils/mappedRows";
import {
  downloadCsvText,
  downloadOutputCsv,
  downloadOutputJson,
} from "../utils/resultsExport";
import styles from "./ImportResultsScreen.module.css";

/**
 * Import Results screen — shown when `ImportResult.status === "committed"`.
 *
 * Displays:
 * - A summary of committed/failed row counts.
 * - A mappings-saved banner if `mappingsSaved > 0` (indicating future imports
 *   for this company will skip the mapping step).
 * - A table of failed rows (from `errors[]`), if any.
 * - A "Download results CSV" action.
 * - A "Start a new import" action that resets the wizard to its initial state.
 */
export function ImportResultsScreen() {
  const { companyCode, state, updateState, isCommitting, commitProgress, commitError } =
    useWizard();
  const navigate = useNavigate();

  const result = state.importResult;

  // Rebuild the fully-mapped data for the downloadable output CSV. Mirrors the
  // Review screen so the download reflects exactly what was imported.
  const preview = state.preview;
  const rows = useMemo(() => preview?.rows ?? [], [preview]);
  const componentColumn =
    state.componentColumn ?? preview?.componentColumn ?? "";

  const componentNames = useMemo(
    () => distinctComponentNames(rows, componentColumn),
    [rows, componentColumn],
  );

  const paramsQuery = useComponentsParameters(
    companyCode,
    componentNames,
    state.mypayEnvironment,
  );
  const mappingsQuery = useCompanyMappings(companyCode);

  const mappedRows = useMemo(
    () =>
      buildMappedRows({
        preview,
        componentColumn,
        startDateColumn: state.startDateColumn,
        endDateColumn: state.endDateColumn,
        paramsByComponent: paramsQuery.data ?? {},
        mappings: mappingsQuery.data ?? [],
      }),
    [
      preview,
      componentColumn,
      state.startDateColumn,
      state.endDateColumn,
      paramsQuery.data,
      mappingsQuery.data,
    ],
  );

  const isExportLoading = paramsQuery.isLoading || mappingsQuery.isLoading;

  const failedRows = result?.errors ?? [];
  const pageSize = state.pageSize ?? DEFAULT_PAGE_SIZE;
  const { page, setPage, pageCount, pageItems, total } = usePagination(
    failedRows,
    pageSize,
  );
  const showPager = total > PAGE_SIZE_OPTIONS[0];

  const handleDownload = () => {
    if (!result) return;
    downloadOutputCsv(mappedRows);
  };

  const handleDownloadJson = () => {
    if (!result) return;
    downloadOutputJson(mappedRows);
  };

  // Failed/skipped source rows captured at commit time (original rows + Reason).
  const failedRowsCsv = state.failedRowsCsv;
  const handleDownloadErrors = () => {
    if (!failedRowsCsv) return;
    downloadCsvText(failedRowsCsv, "pay-component-import-errors.csv");
  };

  const handleNewImport = () => {
    clearWizardSession();
    updateState(newImportWizardState(state));
    void navigate(PAY_COMPONENT_IMPORT_BASE_PATH);
  };

  // Return to the Map Columns step to adjust the (saved) mapping and re-import.
  const handleRemap = () => {
    void navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/map`);
  };

  // A commit failed outright (e.g. scope rejection or workflow failure).
  if (commitError) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <H2>Import Results</H2>
        </div>
        <div className={styles.commitError} role="alert">
          <strong>Import failed:</strong> {commitError}
        </div>
        <ButtonGroup align="right">
          <Button
            variant="tertiary"
            onPress={() => navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/review`)}
          >
            Back to review
          </Button>
          <Button variant="primary" onPress={handleNewImport}>
            Start a new import
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  // Commit in flight — show a live loading/progress view while polling.
  if (isCommitting) {
    const pct = commitProgress
      ? Math.round((commitProgress.chunk / commitProgress.totalChunks) * 100)
      : 0;
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <H2>Importing…</H2>
          <Body>
            Your import is running. This page updates automatically as it
            progresses — you don’t need to wait on the previous screen.
          </Body>
        </div>
        <div className={styles.loading} role="status" aria-live="polite">
          <div className={styles.spinner} aria-hidden="true" />
          {commitProgress && commitProgress.totalChunks > 1 ? (
            <>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${pct}%` }} />
              </div>
              <span className={styles.progressText}>
                Batch {commitProgress.chunk} of {commitProgress.totalChunks} —{" "}
                {commitProgress.committed} committed
                {commitProgress.failed > 0
                  ? `, ${commitProgress.failed} failed`
                  : ""}
              </span>
            </>
          ) : (
            <span className={styles.progressText}>
              Submitting rows to the payroll system…
            </span>
          )}
        </div>
      </div>
    );
  }

  // Guard: should not normally render without a committed result
  if (!result) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <H2>Import Results</H2>
          <Body>No import result available. Please start a new import.</Body>
        </div>
        <ButtonGroup align="right">
          <Button variant="primary" onPress={handleNewImport}>
            Start a new import
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H2>Import Results</H2>
        <Body>
          The import has been committed. Review the summary below and download
          the results if needed.
        </Body>
      </div>

      {/* Summary counts */}
      <section className={styles.summarySection} aria-label="Import summary">
        <div className={styles.summaryGrid}>
          <div
            className={`${styles.summaryCard} ${result.committed > 0 ? styles.summaryCardCommitted : ""}`}
          >
            <span className={styles.summaryCount}>{result.committed}</span>
            <span className={styles.summaryLabel}>Rows committed</span>
          </div>
          <div
            className={`${styles.summaryCard} ${result.failed > 0 ? styles.summaryCardFailed : ""}`}
          >
            <span className={styles.summaryCount}>{result.failed}</span>
            <span className={styles.summaryLabel}>Rows failed</span>
          </div>
        </div>
      </section>

      {/* Mappings saved banner */}
      {result.mappingsSaved > 0 && (
        <div
          className={styles.mappingsBanner}
          role="status"
          aria-label="Mappings saved confirmation"
        >
          <span className={styles.mappingsBannerIcon} aria-hidden="true">
            ✓
          </span>
          <span>
            <strong>
              {result.mappingsSaved} column mapping
              {result.mappingsSaved !== 1 ? "s" : ""} saved.
            </strong>{" "}
            Future imports for this company will skip the mapping step.
          </span>
        </div>
      )}

      {/* Failed rows table */}
      {failedRows.length > 0 && (
        <section
          className={styles.errorsSection}
          aria-labelledby="failed-rows-heading"
        >
          <H3 id="failed-rows-heading">
            {failedRows.length} row{failedRows.length !== 1 ? "s" : ""} failed
          </H3>
          <div
            className={styles.tableWrapper}
            role="region"
            aria-label="Failed rows table"
            tabIndex={0}
          >
            <table className={styles.errorsTable}>
              <thead>
                <tr>
                  <th scope="col" className={styles.th}>
                    Row
                  </th>
                  <th scope="col" className={styles.th}>
                    Employee Code
                  </th>
                  <th scope="col" className={styles.th}>
                    Component
                  </th>
                  <th scope="col" className={styles.th}>
                    Error
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((err, index) => (
                  <tr key={index} className={styles.errorRow}>
                    <td className={styles.tdRow}>{err.row}</td>
                    <td className={styles.tdEmployee}>{err.employeeCode}</td>
                    <td className={styles.tdComponent}>{err.componentCode}</td>
                    <td className={styles.tdError}>{err.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {showPager && (
            <Pagination
              page={page}
              pageCount={pageCount}
              total={total}
              pageSize={pageSize}
              onChange={setPage}
              onPageSizeChange={(n) => updateState({ pageSize: clampPageSize(n) })}
            />
          )}
        </section>
      )}

      {/* Actions */}
      <ButtonGroup align="right">
        <Button
          variant="secondary"
          isDisabled={isExportLoading}
          onPress={handleDownload}
        >
          {isExportLoading ? "Preparing…" : "Download results CSV"}
        </Button>
        <Button
          variant="secondary"
          isDisabled={isExportLoading}
          onPress={handleDownloadJson}
        >
          {isExportLoading ? "Preparing…" : "Download output JSON"}
        </Button>
        {failedRowsCsv && (
          <Button variant="secondary" onPress={handleDownloadErrors}>
            Download errors CSV
          </Button>
        )}
        <Button
          variant="tertiary"
          isDisabled={!state.file}
          onPress={handleRemap}
        >
          Re-map columns
        </Button>
        <Button variant="primary" onPress={handleNewImport}>
          Start a new import
        </Button>
      </ButtonGroup>
    </div>
  );
}
